/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {Injectable, SecurityContext, Type} from '@angular/core';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {TranslateService} from '@ngx-translate/core';
import * as moment from 'moment';

import {DATE_MAX} from '../../../app.constants';
import {Contract} from '../../employees/employees.model';
import {LocalizationService} from '../../localization/localization.service';
import {format, strToDate} from '../../utils/date';
import {isPresent} from '../../utils/lang';
import {Value, ZoneDescriptor} from '../form-generator.interfaces';

import {ActiveEmployeeFieldComponent} from './active-employee-field.component';
import {BlankFieldComponent} from './blank-field.component';
import {BooleanFieldComponent} from './boolean-field/boolean-field.component';
import {BreakLineFieldComponent} from './break-line-field.component';
import {ContractFieldComponent} from './contract-field/contract-field.component';
import {CurrencyFieldComponent} from './currency-field/currency-field.component';
import {DateFieldComponent} from './date-field/date-field.component';
import {DecimalFieldComponent} from './decimal-field/decimal-field.component';
import {EntityFieldComponent} from './entity-field.component';
import {FileFieldComponent} from './file-field/file-field.component';
import {IntegerFieldComponent} from './integer-field/integer-field.component';
import {ListFieldComponent} from './list-field.component';
import {MonthFieldComponent} from './month-field/month-field.component';
import {PeopleTreeComponent} from './people-tree-field.component';
import {PercentageFieldComponent} from './percentage-field.component';
import {PhotoFieldComponent} from './photo-field.component';
import {TableFieldComponent} from './table-field.component';
import {TextFieldComponent} from './text-field/text-field.component';
import {TextAreaFieldComponent} from './textarea-field/textarea-field.component';
import {TreeFieldComponent} from './tree-field/tree-field.component';
import {TransIdListFieldComponent} from './trans-id-list-field.component';

@Injectable()
export class FieldRegistry {

  private static fields: Map<string, Type<any>> = new Map<string, Type<any>>();

  public static initialize() {
    this.fields.set('BOOLEAN', BooleanFieldComponent);
    this.fields.set('INTEGER', IntegerFieldComponent);
    this.fields.set('LABEL', TextFieldComponent);
    this.fields.set('AUTO_GENERATED', TextFieldComponent);
    this.fields.set('COMBINATION', TextFieldComponent);
    this.fields.set('LIST', ListFieldComponent);
    this.fields.set('ENTITY', EntityFieldComponent);
    this.fields.set('ENTITY_PREFIXED', EntityFieldComponent);
    this.fields.set('ENTITY_DATA', EntityFieldComponent);
    this.fields.set('TREE', TreeFieldComponent);
    this.fields.set('TEXT', TextAreaFieldComponent);
    this.fields.set('DATE', DateFieldComponent);
    this.fields.set('MONTH', MonthFieldComponent);
    this.fields.set('PERCENTAGE', PercentageFieldComponent);
    this.fields.set('PHOTO', PhotoFieldComponent);
    this.fields.set('CURRENCY', CurrencyFieldComponent);
    this.fields.set('DECIMAL', DecimalFieldComponent);
    this.fields.set('PEOPLE_TREE', PeopleTreeComponent);
    this.fields.set('WORK_CYCLE_TABLE', TableFieldComponent);
    this.fields.set('ACTIVE_EMPLOYEE', ActiveEmployeeFieldComponent);
    this.fields.set('BREAK_LINE', BreakLineFieldComponent);
    this.fields.set('BLANK', BlankFieldComponent);
    this.fields.set('FILE', FileFieldComponent);
    this.fields.set('CONTRACT', ContractFieldComponent);

    // Faked :
    this.fields.set('GRAPH_MONTH', TextFieldComponent);
    this.fields.set('PAY_PERIOD', TextFieldComponent);
    this.fields.set('OTHER', TextFieldComponent);
  }

  constructor(private localization: LocalizationService,
              private translate: TranslateService,
              private sanitizer: DomSanitizer) {
  }

  public resolve(descriptor: ZoneDescriptor): Type<any> {
    // Special case for break line internal type
    if (descriptor.internalType === 'BREAK_LINE') {
      return BreakLineFieldComponent;
    }

    if (!FieldRegistry.fields.has(descriptor.type)) {
      return null;
    }


    if (descriptor.type === 'TREE') {
      // if there are duplicates in rep tree, try to fallback display as a transId based list
      const values = (descriptor.metadatas.items ?? []).map(item => item.value);
      if (values.some((element, index) => {
        return values.indexOf(element) !== index;
      })) {
        descriptor.metadatas.transIdList = true;
        return TransIdListFieldComponent;
      }
    }

    return FieldRegistry.fields.get(descriptor.type);
  }

  public getDisplayValue(value: Value, descriptor: ZoneDescriptor, currency?: string, items?: any): SafeHtml {
    if (!value) {
      return '';
    }

    if (value.type === 'BREAK_LINE') {
      return '<hr>';
    }

    if (value.type === 'BLANK') {
      return '';
    }

    if (!isPresent(value) || !isPresent(value.value) || value.value === '') {
      return '-';
    }

    let html: string;

    switch (value.type) {
      case 'BREAK_LINE':
        html = '<hr>';
        break;
      case 'BLANK':
        html = '';
        break;
      case 'FILE':
        html = value.file ? value.file.filename : '-';
        break;
      case 'BOOLEAN':
        html = value.value ? '<i class="fa fa-light fa-check" aria-hidden="true"></i>' : value.value === false
          ? '<i class="fa fa-light fa-times" aria-hidden="true"></i>' : '-';
        break;
      case 'PERCENTAGE':
        html = this.localization.localizeNumber(
          parseFloat(value.value) / 100, 'PERCENT', undefined, undefined, descriptor.nbDigitsAfterDecimal || 2
        );
        break;
      case 'INTEGER':
        html = this.localization.localizeNumber(value.value, 'INTEGER');
        break;
      case 'DECIMAL':
        html = this.localization.localizeNumber(
          value.value, 'DECIMAL', undefined, currency, descriptor.nbDigitsAfterDecimal || 2
        );
        break;
      case 'CURRENCY':
        html = this.localization.localizeNumber(
          value.value, 'CURRENCY', undefined, currency, descriptor.nbDigitsAfterDecimal || 2
        );
        break;
      case 'LIST':
      case 'TREE': {
        // if value has a transId and this transId is matching display for this value, use it
        if (value.transId && descriptor.metadatas.items?.some(
          item => item.value === value.value && item.display === value.transId)
        ) {
          return this.translate.instant(value.transId);
        }
        const entity = descriptor.metadatas.items?.find(item => item.value === value.value);
        if (entity) {
          html = this.translate.instant(entity.display);
        } else {
          html = value.value || '-';
        }
        break;
      }
      case 'MONTH':
      case 'DATE':
        html = format(strToDate(value.value), this.localization.dateFormat);
        break;
      case 'ENTITY':
      case 'ENTITY_DATA':
      case 'ENTITY_PREFIXED':
        html = this.translate.instant((value.transId || value.value + ''));
        break;
      case 'PEOPLE_TREE':
        const node = (descriptor.metadatas && descriptor.metadatas.items)
          ? descriptor.metadatas.items.find(item => item.id === value.value)
          : undefined;
        html = node ? `${node.nodeName}` : value.transId || '-';
        break;
      case 'ACTIVE_EMPLOYEE':
        const tree = (descriptor.metadatas && descriptor.metadatas.items)
          ? descriptor.metadatas.items.find(item => item.id === value.value)
          : undefined;
        if (tree) {
          html = `${tree.firstName} ${tree.lastName}`.trim();
        } else {
          html = value.transId || '-';
        }
        break;
      case 'CONTRACT':
        const selectedContract = items.find(contract => contract.id === value.value);
        html = selectedContract ? this.customPlaceholderGenerator(selectedContract) : value.value;
        if (selectedContract) {
          html = this.customPlaceholderGenerator(selectedContract);
        } else {
          html = value.value;
          descriptor.readOnly = true;
        }
        break;
      case 'WORK_CYCLE_TABLE':
        html = this.buildTable(value.value, descriptor.metadatas.startDate, descriptor.metadatas.typeOfItems, descriptor.metadatas.endDate);
        break;
      case 'LABEL':
      case 'TEXT':
      default:
        html = value.value;
        break;
    }

    return this.sanitizer.sanitize(SecurityContext.HTML, html);
  }

  private buildTable(value: number[], startDate: string, typeOfItems: string[], endDate: string): string {
    // TODO : complete temporary fix of undefined bug in workcycle
    if (endDate && startDate) {
      let daysInBetween = moment(endDate).diff(moment(startDate), 'days') + 1;
      daysInBetween = daysInBetween > 0 ? daysInBetween : 0;
      value = value.slice(0, daysInBetween);
    }
    return `
    <div style="width: 100%; overflow: auto;">
    <table class="handsontable direction-ltr" border="1"><thead>
       <tr>
       ${value.map((v, i) => `<th width="30px" class="text-center">
            ${moment(startDate || undefined).add(i, 'days').format(value.length > 7 ? 'dd-D-MMM' : 'dd').split('-').join('<br/>')}
                              </th>`).join('')}
       </tr>
    </thead>
    <tbody>
      <tr>
      ${value.map((v, index: number) =>
      `<td width="30px" ${v === 0 ? 'style="background: #F9FFA3"' : ''} class="text-center">${
        (v === 0 && typeOfItems && ['OFF', 'SUN', 'SAT'].includes(typeOfItems[index])) || v === 25 ? '-' : v !== undefined ? v : '-'
      }</td>`
    ).join('')}
      </tr>
    </tbody></table>
    </div>
    `;
  }

  public customPlaceholderGenerator(contract: Contract) {
    const text: string = this.translate.instant('EMPLOYEE_FORM.Contract_name', {
      name: contract.cliRegNickName
    });

    return `${text} ${this.getContractDate(contract)}`;
  }

  public getContractDate(contract: Contract): string {
    return `(${
      format(strToDate(contract.begindate), this.localization.dateFormat)
    } - ${
      contract.enddate === DATE_MAX || !contract.enddate
        ? '...'
        : format(strToDate(contract.enddate), this.localization.dateFormat)
    })`;
  }
}

FieldRegistry.initialize();
